import dayjs from 'dayjs'
import { PriceRank } from '@/models/room-rate/price-rank'

/** 0=Sun ... 6=Sat（JS Date#getDay / BE daysOfWeek と同じ） */
export type WeekdayIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6

/** GET /rate-calendars の1日分。BE が初期値解決済みで月の全日を返す */
export type RateCalendarEntry = {
  date: string
  /** 表示用ランク。ランク未登録時のみ null */
  priceRankId: string | null
  memo: string | null
  /** true = DB にカレンダー行あり / false = 初期値表示 */
  isSaved: boolean
  /** 国民の祝日（振替休日を含む）。日付を赤で表示する */
  isNationalHoliday: boolean
}

export type RateCalendarUpdateInput = {
  date: string
  priceRankId: string
  memo?: string | null
}

export type RateCalendarBulkInput = {
  from: string
  to: string
  daysOfWeek: WeekdayIndex[]
  priceRankId: string
}

export type RateCalendarDay = RateCalendarEntry

export type RateCalendarDayMap = Record<string, RateCalendarDay>

export const RATE_CALENDAR_WEEKDAY_KEYS: WeekdayIndex[] = [0, 1, 2, 3, 4, 5, 6]

const pad2 = (n: number) => String(n).padStart(2, '0')

export const toDateKey = (year: number, month: number, day: number) => `${year}-${pad2(month)}-${pad2(day)}`

export const parseDateKey = (dateKey: string) => {
  const [y, m, d] = dateKey.split('-').map(Number)
  return { year: y, month: m, day: d }
}

export const RATE_CALENDAR_WEEKDAY_TEXT_COLORS = {
  default: '#272727',
  sunday: '#D95F4A',
  saturday: '#5C93D6',
  muted: '#676767',
} as const

export const getRateCalendarWeekdayTextColor = (weekday: number, options?: { muted?: boolean }): string => {
  if (options?.muted) {
    return RATE_CALENDAR_WEEKDAY_TEXT_COLORS.muted
  }
  if (weekday === 0) {
    return RATE_CALENDAR_WEEKDAY_TEXT_COLORS.sunday
  }
  if (weekday === 6) {
    return RATE_CALENDAR_WEEKDAY_TEXT_COLORS.saturday
  }
  return RATE_CALENDAR_WEEKDAY_TEXT_COLORS.default
}

export const toRateCalendarDayMap = (entries: RateCalendarEntry[]): RateCalendarDayMap =>
  entries.reduce<RateCalendarDayMap>((acc, entry) => {
    acc[entry.date] = {
      date: entry.date,
      priceRankId: entry.priceRankId ?? null,
      memo: entry.memo ?? null,
      // P05 契約: isSaved は必須。欠落時は未保存扱い（旧 sparse 互換の ?? true は捨てる）
      isSaved: entry.isSaved === true,
      // 欠落時（バックエンド未反映）は祝日扱いしない
      isNationalHoliday: entry.isNationalHoliday === true,
    }
    return acc
  }, {})

export const getMinEditableDateKey = (now = dayjs()): string => now.subtract(1, 'day').format('YYYY-MM-DD')

/** 昨日以降は編集可。一昨日以前は不可 */
export const isRateCalendarDateEditable = (dateKey: string, now = dayjs()): boolean => {
  return !dayjs(dateKey).startOf('day').isBefore(now.subtract(1, 'day').startOf('day'))
}

/**
 * セル表示用ランク解決。
 * priceRankId はあるが ranks に無い場合は orphan（削除済み等）として扱い、未設定と区別する。
 * 平日／休日／休前日の判定は FE では行わない。
 */
export type CalendarCellRankDisplay =
  { status: 'empty' } | { status: 'matched'; rank: PriceRank } | { status: 'orphan'; priceRankId: string }

export const resolveCalendarCellDisplay = (dateKey: string, dayMap: RateCalendarDayMap, ranks: PriceRank[]): CalendarCellRankDisplay => {
  const priceRankId = dayMap[dateKey]?.priceRankId
  if (!priceRankId) {
    return { status: 'empty' }
  }
  const rank = ranks.find(item => item.id === priceRankId)
  if (!rank) {
    return { status: 'orphan', priceRankId }
  }
  return { status: 'matched', rank }
}

export type CalendarCell = {
  dateKey: string
  day: number
  isCurrentMonth: boolean
  weekday: WeekdayIndex
}

/** 日曜始まりの月グリッド（6週×7日） */
export const buildMonthGrid = (year: number, month: number): CalendarCell[] => {
  const first = new Date(year, month - 1, 1)
  const startOffset = first.getDay()
  const daysInMonth = new Date(year, month, 0).getDate()
  const prevMonthDays = new Date(year, month - 1, 0).getDate()

  const cells: CalendarCell[] = []

  for (let i = 0; i < 42; i++) {
    const dayIndex = i - startOffset + 1
    if (dayIndex < 1) {
      const day = prevMonthDays + dayIndex
      const prevMonth = month === 1 ? 12 : month - 1
      const prevYear = month === 1 ? year - 1 : year
      cells.push({
        dateKey: toDateKey(prevYear, prevMonth, day),
        day,
        isCurrentMonth: false,
        weekday: (i % 7) as WeekdayIndex,
      })
    } else if (dayIndex > daysInMonth) {
      const day = dayIndex - daysInMonth
      const nextMonth = month === 12 ? 1 : month + 1
      const nextYear = month === 12 ? year + 1 : year
      cells.push({
        dateKey: toDateKey(nextYear, nextMonth, day),
        day,
        isCurrentMonth: false,
        weekday: (i % 7) as WeekdayIndex,
      })
    } else {
      cells.push({
        dateKey: toDateKey(year, month, dayIndex),
        day: dayIndex,
        isCurrentMonth: true,
        weekday: (i % 7) as WeekdayIndex,
      })
    }
  }

  return cells
}
